import { CircleFadingArrowUp, MoveUpRight, SquareActivity } from 'lucide-react';
import { signIn } from 'next-auth/react';
import { useSetRecoilState } from 'recoil';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { SavedItemButton } from '@/components/ui/saved-item-button';
import { useSavedItems } from '@/hooks/use-saved-items';
import { MAX_SAVED_RATES, SAVED_ITEM_TYPE } from '@/lib/constants';
import { MarketRateModel, MarketRateQueryModel } from '@/lib/models/rates/types';
import { lockedDialogContentState, lockedDialogIsOpenState } from '@/state/user';
import { SegmentModeType } from '@/types/api-types';
import { getUpgradeAccountUrl } from '@/utils/helpers/urls';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { RatesMarketRateDetails } from '../market-rate-details';

export interface RatesMarketRatePopoutProps {
  mode: SegmentModeType;
  rateQuery: MarketRateQueryModel;
  rates?: MarketRateModel;
  upgradeRequired?: boolean;
  isNotSignedIn?: boolean;
  canSaveRates?: boolean;
}

const RatesMarketRatePopout = ({
  mode,
  rates,
  rateQuery,
  upgradeRequired,
  isNotSignedIn,
  canSaveRates = false,
}: RatesMarketRatePopoutProps) => {
  trackUser.event('MarketRates: Popout open', { mode, isLocked: upgradeRequired });
  const setLockedDialogIsOpen = useSetRecoilState(lockedDialogIsOpenState);
  const setLockedDialogContent = useSetRecoilState(lockedDialogContentState);

  const { savedItemsCount, createSavedItem, getSavedItemByValue, deleteSavedItem } = useSavedItems({
    types: [SAVED_ITEM_TYPE.MARKET_RATE],
  });

  const canAddMoreRates = savedItemsCount < MAX_SAVED_RATES;

  const savedItem = rateQuery.id
    ? getSavedItemByValue({ key: 'id', value: rateQuery.id, type: SAVED_ITEM_TYPE.MARKET_RATE })
    : undefined;

  const handleClickUpgrade = () => {
    trackUser.event('Go to: Upgrade Account', {
      location: 'Market Rate Popout',
    });
  };

  const handleClickSignin = () => {
    signIn();

    trackUser.event('Go to: Sign-in', {
      location: 'Market Rate Popout',
    });
  };

  const handleOnSaveRate = () => {
    trackUser.event('MarketRates: Watch rate clicked', { mode });

    if (savedItem) {
      deleteSavedItem(savedItem);
    } else {
      if (!canAddMoreRates) {
        trackUser.event('MarketRates: Watch rate limit reached', { mode });
        // Show alert to user
        setLockedDialogContent({
          title: 'You have reached the limit of how many market rates you can save.',
          description: `Your account can have ${MAX_SAVED_RATES} saved rates at a time. You'll have to remove an existing rate to save this one.`,
        });
        setLockedDialogIsOpen(true);
        return;
      }

      // Create saved item if allowed
      createSavedItem({
        data: rateQuery,
        label: rateQuery.label || '',
        type: SAVED_ITEM_TYPE.MARKET_RATE,
      });
    }
  };

  const { base, header, textWithIcon, headerIcon, headerText, content } = styles();

  return (
    <article className={base()}>
      <header className={header()}>
        <div className={textWithIcon()}>
          <SquareActivity className={headerIcon()} />
          <Heading as="h3" variant="h5" className={headerText()}>
            Market rate
          </Heading>
        </div>
        {canSaveRates && (
          <SavedItemButton
            onClick={handleOnSaveRate}
            isSaved={!!savedItem}
            variant="small"
            type={SAVED_ITEM_TYPE.MARKET_RATE}
            label={{ saved: 'Unwatch', notSaved: 'Watch rate' }}
          />
        )}
      </header>
      <div className={content()}>
        {upgradeRequired ? (
          <div className="-m-4 rounded-b-md bg-states-warning-backgroundColor p-4">
            <div className={textWithIcon()}>
              <CircleFadingArrowUp className="size-4" />
              <Heading variant="h5" className="mb-1 text-baseSm font-medium">
                Account upgrade required
              </Heading>
            </div>
            <Text variant="secondary" className="mb-3 text-sm leading-snug">
              Upgrade your plan to a Team or Pro plan get access to market rate indexes for ocean and air routes.
            </Text>
            {isNotSignedIn ? (
              <Button size="small" iconRight={<MoveUpRight className="size-4" />} onClick={() => handleClickSignin()}>
                Sign up for an account
              </Button>
            ) : (
              <Button
                size="small"
                iconRight={<MoveUpRight className="size-4" />}
                onClick={() => handleClickUpgrade()}
                href={getUpgradeAccountUrl()}
              >
                See upgrade options
              </Button>
            )}
          </div>
        ) : (
          <RatesMarketRateDetails rates={rates} mode={mode} variant="md" shouldShowFooter shouldShowChartLegend />
        )}
      </div>
    </article>
  );
};

const styles = tv({
  slots: {
    base: 'text-left',
    content: 'relative block p-4',
    header: 'flex items-center justify-between gap-1.5 border-b border-grey-200 px-4 py-3',
    textWithIcon: 'flex items-center gap-1.5',
    headerText: 'm-0 font-medium',
    headerIcon: 'size-4',
    definitionList: 'm-0 grid grid-cols-2 text-sm text-text-secondary last-of-type:mb-0',
    definitionTitle: 'm-0 flex min-w-44 items-center p-0',
    definitionDescription: 'justify-right m-0 flex items-center p-0 text-right',
  },
});

export { RatesMarketRatePopout };
